/* ================================================================
   LOCATION PAGE DATA — Garvaana Enterprise
   One entry per city. Each powers a dedicated SEO landing page at
   /landscape-design-<city>. Content is intentionally UNIQUE per city
   (local sectors, real project references, area-specific copy) so
   Google treats each as a genuine local page — not a doorway clone.

   Keyword source: Garvaana_SEO_Keywords_500.xlsx → "Local · <City>"
   and "Top 50 Money Keywords".
================================================================ */

export const LOCATIONS = [
  /* ── NOIDA — easiest local win (their studio is in Sector 44) ── */
  {
    slug: "landscape-design-noida",
    city: "Noida",
    region: "Uttar Pradesh",
    priority: 0.9,
    title: "Landscape Design in Noida | Garden Designers — Garvaana",
    description:
      "Garvaana — top landscape designers in Noida. Villa gardens, terrace & rooftop gardens, society landscaping & garden maintenance across all sectors. Free consultation: +91-9800040505.",
    keywords:
      "landscape design in Noida, landscape designers in Noida, landscape services in Noida, landscaping company in Noida, garden designers in Noida, garden design services in Noida, garden contractors in Noida, garden maintenance in Noida, best landscape designers in Noida, top landscape designers in Noida, luxury landscape design in Noida, rooftop garden designer in Noida, terrace garden designer in Noida, vertical garden Noida, villa landscape design Noida, society garden maintenance Noida, villa garden design Sector 150 Noida",
    heroLabel: "Serving All of Noida",
    h1: "Landscape Design in Noida",
    intro: [
      "Garvaana Enterprise is a luxury landscape design and garden management company serving Noida from our studio in Sector 44. From high-rise terrace gardens to sprawling villa landscapes, we design, build and maintain extraordinary green spaces across every sector of the city.",
      "Whether you own an apartment in Sector 150, a villa in Sector 44–50, or manage a corporate campus in Sector 62, our team of horticulturists and landscape architects delivers bespoke designs, premium plants from our 500+ variety nursery, and reliable year-round maintenance.",
    ],
    localities: [
      "Sector 44, 45 & 50",
      "Sector 93 & 100 (Expressway)",
      "Sector 128 & 137",
      "Sector 150 (Sports City)",
      "Sector 62 & 63 (Corporate)",
      "Noida Extension / Greater Noida West",
    ],
    featuredProject: {
      name: "Serenity Sky Residences — Sector 150",
      blurb:
        "We are currently designing landscaped entrance promenades, a sensory children's garden, rooftop terrace retreats and a community amphitheatre for this premium Sector 150 housing society.",
    },
    services: [
      "Villa & apartment garden design",
      "Terrace & rooftop garden design",
      "Society & RWA garden maintenance (AMC)",
      "Vertical green walls",
      "Drip & automatic irrigation",
      "Lawn installation & turf laying",
    ],
    faqs: [
      {
        q: "Do you provide landscape design services across all sectors of Noida?",
        a: "Yes. Garvaana serves the whole of Noida — including Sector 44, 50, 93, 100, 128, 137 and Sector 150 — as well as Greater Noida and Noida Extension. We design villa gardens, terrace gardens, society landscapes and corporate green spaces.",
      },
      {
        q: "How much does landscape design cost in Noida?",
        a: "Cost depends on area, design complexity and materials. Terrace gardens, villa landscapes and society projects are all quoted individually. Call or WhatsApp +91-9800040505 for a free consultation and a written quote within 24 hours.",
      },
      {
        q: "Do you offer garden maintenance contracts (AMC) for societies in Noida?",
        a: "Yes — we offer monthly, quarterly and annual garden maintenance contracts for housing societies, RWAs, villas and corporate campuses across Noida, covering pruning, fertilisation, pest management and seasonal care.",
      },
    ],
  },

  /* ── GURUGRAM — highest-budget client base ── */
  {
    slug: "landscape-design-gurugram",
    city: "Gurugram",
    region: "Haryana",
    priority: 0.9,
    title: "Landscape Design in Gurugram | Villa Gardens — Garvaana",
    description:
      "Garvaana — best landscape designers in Gurugram. Luxury villa & farmhouse gardens, swimming pool landscaping, terrace gardens & garden maintenance in DLF, Golf Course Road & beyond. Call +91-9800040505.",
    keywords:
      "landscape design in Gurugram, landscape designers in Gurugram, landscape services in Gurugram, landscaping company in Gurugram, garden designers in Gurugram, garden design services in Gurugram, garden contractors in Gurugram, garden maintenance in Gurugram, best landscape designers in Gurugram, top landscape designers in Gurugram, luxury landscape design in Gurugram, villa landscape design Gurugram, farmhouse landscaping Gurugram, swimming pool landscaping Gurugram, rooftop garden designer in Gurugram, terrace garden designer in Gurugram, vertical garden Gurugram, society garden maintenance Gurugram, garden lighting Gurugram",
    heroLabel: "Serving Gurugram & DLF",
    h1: "Landscape Design in Gurugram",
    intro: [
      "Garvaana Enterprise is Gurugram's premier luxury landscape design company, crafting bespoke villa gardens, farmhouse landscapes and resort-grade outdoor spaces for the city's most discerning homeowners and developers.",
      "From private infinity lawns near DLF Golf Links to swimming-pool gardens, mood-lit cabanas and sculptural water walls, we handle design, installation and long-term maintenance — using imported ornamental grasses, specimen palms and premium hardscape throughout.",
    ],
    localities: [
      "DLF Phase 1–5 & Golf Course Road",
      "Sushant Lok & Nirvana Country",
      "Sohna Road & Golf Course Ext. Road",
      "Sector 57–58",
      "MG Road & Cyber City (Corporate)",
      "Gwal Pahari farmhouse belt",
    ],
    featuredProject: {
      name: "Gurugram Golf Estate Villa — DLF Golf Links",
      blurb:
        "An ongoing luxury garden for a villa adjacent to DLF Golf Links — private infinity lawn, imported ornamental grasses, a tropical cabana garden, evening mood-lighting and a sculptural water wall.",
    },
    services: [
      "Luxury villa & farmhouse garden design",
      "Swimming pool & poolside landscaping",
      "Garden lighting & mood-lighting design",
      "Water features, koi ponds & fountains",
      "Terrace & rooftop gardens",
      "Estate garden maintenance",
    ],
    faqs: [
      {
        q: "Which areas of Gurugram does Garvaana serve?",
        a: "We serve all of Gurugram — DLF Phases 1–5, Golf Course Road, Golf Course Extension Road, Sohna Road, Sushant Lok, Nirvana Country, Sector 57–58 and the Gwal Pahari farmhouse belt — for villa gardens, farmhouse landscaping and corporate green spaces.",
      },
      {
        q: "Do you design swimming pool landscaping and farmhouse gardens in Gurugram?",
        a: "Yes. Swimming-pool landscaping and farmhouse gardens are among our most-requested Gurugram services — including poolside planting palettes, cabana gardens, lawns, pergolas, fire pits and outdoor lighting.",
      },
      {
        q: "What is the typical budget for a luxury villa garden in Gurugram?",
        a: "Luxury villa gardens vary widely with size and features (water walls, lighting, imported plants). We provide a transparent written quote after a free site consultation — call or WhatsApp +91-9800040505.",
      },
    ],
  },

  /* ── DELHI — high volume ── */
  {
    slug: "landscape-design-delhi",
    city: "Delhi",
    region: "Delhi NCR",
    priority: 0.9,
    title: "Landscape Design in Delhi | Garden Designers — Garvaana",
    description:
      "Garvaana — best landscape designers in Delhi. Bungalow & villa gardens, terrace gardens, heritage garden restoration, vertical walls & garden maintenance across South & North Delhi. Call +91-9800040505.",
    keywords:
      "landscape design in Delhi, landscape designers in Delhi, landscape services in Delhi, landscaping company in Delhi, landscape architects in Delhi, garden designers in Delhi, garden design services in Delhi, garden contractors in Delhi, garden maintenance in Delhi, best landscape designers in Delhi, best landscape company in Delhi, top landscape designers in Delhi, luxury landscape design in Delhi, rooftop garden designer in Delhi, terrace garden designer in Delhi, vertical garden Delhi, garden lighting Delhi, lawn installation Delhi, garden contractor Delhi",
    heroLabel: "Serving All of Delhi",
    h1: "Landscape Design in Delhi",
    intro: [
      "Garvaana Enterprise delivers luxury landscape design and garden management across Delhi — from Lutyens-zone heritage bungalow restorations to contemporary terrace gardens in South Delhi apartments and large community green belts in North Delhi.",
      "Our landscape architects and horticulturists handle the full journey: 3D design, installation, premium planting from our 500+ variety nursery, and year-round maintenance contracts for homes, farmhouses, offices and residential societies.",
    ],
    localities: [
      "South Delhi (Vasant Vihar, GK, Defence Colony)",
      "Lutyens' Delhi & central bungalow zone",
      "West Delhi (Punjabi Bagh, Rajouri)",
      "North Delhi & Rohini",
      "Dwarka",
      "Chattarpur farmhouse belt",
    ],
    featuredProject: {
      name: "Delhi Heritage Bungalow — Lutyens' Zone",
      blurb:
        "A sensitive restoration of a colonial-era bungalow garden — preserving 80-year-old specimen trees and reintroducing heritage rose varieties and period-appropriate planting.",
    },
    services: [
      "Bungalow & villa garden design",
      "Heritage garden restoration",
      "Terrace & rooftop gardens",
      "Vertical green walls",
      "Garden lighting & water features",
      "Garden maintenance contracts",
    ],
    faqs: [
      {
        q: "Does Garvaana provide landscape design across all of Delhi?",
        a: "Yes — we serve South Delhi, Lutyens' Delhi, West Delhi, North Delhi, Rohini, Dwarka and the Chattarpur farmhouse belt, designing bungalow gardens, terrace gardens, heritage restorations and society landscapes.",
      },
      {
        q: "Can you restore an old or heritage garden in Delhi?",
        a: "Absolutely. Heritage garden restoration is a specialty — we preserve mature specimen trees, reintroduce period-appropriate planting and modernise irrigation while honouring the property's character, as we did for a Lutyens-zone bungalow.",
      },
      {
        q: "Do you offer terrace and rooftop garden design in Delhi apartments?",
        a: "Yes. We design structurally safe, waterproofed terrace and rooftop gardens with lightweight growing media, planters, pergolas and ambient lighting — ideal for Delhi apartments and penthouses.",
      },
    ],
  },

  /* ── FARIDABAD — underserved NCR pocket ── */
  {
    slug: "landscape-design-faridabad",
    city: "Faridabad",
    region: "Haryana",
    priority: 0.8,
    title: "Landscape Design in Faridabad | Garden Services — Garvaana",
    description:
      "Garvaana — landscape designers in Faridabad. Villa & farmhouse gardens, resort landscaping, terrace gardens & garden maintenance across Faridabad & Surajkund. Free consultation: +91-9800040505.",
    keywords:
      "landscape design in Faridabad, landscape designers in Faridabad, landscape services in Faridabad, landscaping company in Faridabad, garden designers in Faridabad, garden design services in Faridabad, garden contractors in Faridabad, garden maintenance in Faridabad, best landscape designers in Faridabad, top landscape designers in Faridabad, luxury landscape design in Faridabad, villa landscape design Faridabad, farmhouse landscaping Faridabad, resort landscape design",
    heroLabel: "Serving Faridabad & Surajkund",
    h1: "Landscape Design in Faridabad",
    intro: [
      "Garvaana Enterprise brings luxury landscape design and garden management to Faridabad — from private villa gardens and farmhouse estates to hospitality and resort landscaping around the Surajkund belt.",
      "We design, build and maintain lush outdoor spaces with premium plants, hardscape, water features and smart irrigation, backed by reliable maintenance contracts for homes, societies and commercial properties.",
    ],
    localities: [
      "Surajkund & Anangpur",
      "Sector 14–21 (Old Faridabad)",
      "Greater Faridabad (Neharpar)",
      "Sector 75–89",
      "Ballabhgarh",
      "NH-19 farmhouse belt",
    ],
    featuredProject: {
      name: "Heritage Boutique Resort — Surajkund",
      blurb:
        "A full resort landscape transformation — poolside tropical gardens, illuminated evening pathways, a fragrance garden and curated seasonal flowering collections.",
    },
    services: [
      "Villa & farmhouse garden design",
      "Resort & hospitality landscaping",
      "Terrace & rooftop gardens",
      "Lawn installation & turf laying",
      "Water features & garden lighting",
      "Garden maintenance contracts",
    ],
    faqs: [
      {
        q: "Does Garvaana provide landscape services in Faridabad?",
        a: "Yes. We design and maintain gardens across Faridabad — Old Faridabad, Greater Faridabad (Neharpar), Surajkund, Ballabhgarh and the NH-19 farmhouse belt — for villas, farmhouses, resorts and societies.",
      },
      {
        q: "Do you design resort and hospitality gardens near Surajkund?",
        a: "Yes — we have delivered full resort landscapes in the Surajkund area, including poolside gardens, fragrance gardens, illuminated pathways and seasonal flowering displays.",
      },
      {
        q: "How do I get a quote for a garden in Faridabad?",
        a: "Call or WhatsApp +91-9800040505 for a free consultation. We visit the site, understand your vision and send a written quote within 24 hours.",
      },
    ],
  },

  /* ── GHAZIABAD — underserved NCR pocket ── */
  {
    slug: "landscape-design-ghaziabad",
    city: "Ghaziabad",
    region: "Uttar Pradesh",
    priority: 0.8,
    title: "Landscape Design in Ghaziabad | Garden Services — Garvaana",
    description:
      "Garvaana — landscape designers in Ghaziabad. Farmhouse & villa gardens, society landscaping, terrace gardens & garden maintenance across Indirapuram, Raj Nagar & Crossing Republik. Call +91-9800040505.",
    keywords:
      "landscape design in Ghaziabad, landscape designers in Ghaziabad, landscape services in Ghaziabad, landscaping company in Ghaziabad, garden designers in Ghaziabad, garden design services in Ghaziabad, garden contractors in Ghaziabad, garden maintenance in Ghaziabad, best landscape designers in Ghaziabad, top landscape designers in Ghaziabad, luxury landscape design in Ghaziabad, farmhouse landscape design Ghaziabad, society garden maintenance Ghaziabad",
    heroLabel: "Serving Ghaziabad",
    h1: "Landscape Design in Ghaziabad",
    intro: [
      "Garvaana Enterprise designs and maintains beautiful gardens across Ghaziabad — from sprawling farmhouse estates in Crossing Republik to society landscapes and apartment terrace gardens in Indirapuram and Raj Nagar Extension.",
      "Our team handles complete garden creation — lawns, orchards, pathways, pergolas, water features and irrigation — plus dependable maintenance contracts for homes, RWAs and commercial spaces.",
    ],
    localities: [
      "Indirapuram",
      "Raj Nagar & Raj Nagar Extension",
      "Vaishali & Vasundhara",
      "Crossing Republik",
      "Kaushambi",
      "NH-9 / NH-24 belt",
    ],
    featuredProject: {
      name: "Ghaziabad Farmhouse Estate — Crossing Republik",
      blurb:
        "A sprawling farmhouse landscape — heritage fruit orchard, organic kitchen garden, wildflower meadows, rustic stone pathways and a wooden pergola with a fire-pit seating area.",
    },
    services: [
      "Farmhouse & villa garden design",
      "Society & RWA landscaping + AMC",
      "Terrace & rooftop gardens",
      "Organic & kitchen gardens",
      "Lawn installation & irrigation",
      "Garden maintenance contracts",
    ],
    faqs: [
      {
        q: "Does Garvaana serve Ghaziabad for landscape and garden design?",
        a: "Yes — we cover Indirapuram, Raj Nagar, Raj Nagar Extension, Vaishali, Vasundhara, Crossing Republik and Kaushambi, designing farmhouse gardens, society landscapes and terrace gardens.",
      },
      {
        q: "Do you build farmhouse gardens and orchards in Ghaziabad?",
        a: "Yes. We design complete farmhouse landscapes — fruit orchards, organic kitchen gardens, lawns, stone pathways, pergolas and fire-pit seating — as delivered for an estate in Crossing Republik.",
      },
      {
        q: "Do you offer society garden maintenance in Ghaziabad?",
        a: "Yes — monthly, quarterly and annual maintenance contracts for housing societies, RWAs and apartment complexes across Ghaziabad.",
      },
    ],
  },

  /* ── GREATER NOIDA — growing market, township project ── */
  {
    slug: "landscape-design-greater-noida",
    city: "Greater Noida",
    region: "Uttar Pradesh",
    priority: 0.8,
    title: "Landscape Design in Greater Noida — Garvaana Enterprise",
    description:
      "Garvaana — landscape designers in Greater Noida. Township master-planning, villa gardens, society landscaping & garden maintenance across Greater Noida & Greater Noida West. Call +91-9800040505.",
    keywords:
      "landscape design in Greater Noida, landscape designers in Greater Noida, landscape services in Greater Noida, landscaping company in Greater Noida, garden designers in Greater Noida, garden design services in Greater Noida, garden contractors in Greater Noida, garden maintenance in Greater Noida, best landscape designers in Greater Noida, top landscape designers in Greater Noida, luxury landscape design in Greater Noida, township landscape master plan, society garden maintenance Greater Noida",
    heroLabel: "Serving Greater Noida & West",
    h1: "Landscape Design in Greater Noida",
    intro: [
      "Garvaana Enterprise provides landscape design and garden management across Greater Noida and Greater Noida West (Noida Extension) — from township master-planning and society common-area landscaping to private villa gardens.",
      "We specialise in large-scale green infrastructure — parks, jogging trails, avenue plantations, water features and themed gardens — alongside full horticultural maintenance protocols for developers, RWAs and homeowners.",
    ],
    localities: [
      "Greater Noida West (Noida Extension)",
      "Alpha, Beta & Gamma sectors",
      "Pari Chowk & Knowledge Park",
      "Techzone IV",
      "Jaypee Greens belt",
      "Sector 1–4 (Noida Ext.)",
    ],
    featuredProject: {
      name: "The Willow Creek Township — Greater Noida West",
      blurb:
        "An ongoing landscape master plan for an integrated township — parks, jogging trails, water features, avenue plantations, themed gardens, children's zones and full horticultural management.",
    },
    services: [
      "Township landscape master-planning",
      "Society & common-area landscaping",
      "Villa & apartment garden design",
      "Avenue plantation & parks",
      "Irrigation & water features",
      "Garden maintenance contracts",
    ],
    faqs: [
      {
        q: "Does Garvaana handle township and society landscaping in Greater Noida?",
        a: "Yes — township master-planning and society common-area landscaping are core strengths. We are currently delivering a full landscape master plan for an integrated township in Greater Noida West.",
      },
      {
        q: "Do you serve Greater Noida West / Noida Extension?",
        a: "Yes. We cover Greater Noida West (Noida Extension), the Alpha/Beta/Gamma sectors, Pari Chowk, Knowledge Park and Techzone IV for both residential and large-scale projects.",
      },
      {
        q: "Can you provide ongoing maintenance for a society in Greater Noida?",
        a: "Yes — we offer annual maintenance contracts covering parks, lawns, avenue trees and common-area planting for societies and townships across Greater Noida.",
      },
    ],
  },

  /* ── MEERUT — extended reach ── */
  {
    slug: "landscape-design-meerut",
    city: "Meerut",
    region: "Uttar Pradesh",
    priority: 0.7,
    title: "Landscape Design in Meerut | Garden Designers — Garvaana",
    description:
      "Garvaana — landscape designers in Meerut. Villa & farmhouse gardens, terrace gardens, lawn installation & garden maintenance across Meerut. Free consultation: +91-9800040505.",
    keywords:
      "landscape designers in Meerut, landscape services in Meerut, garden designers in Meerut, garden maintenance in Meerut, best landscape designers in Meerut, landscape design in Meerut, villa garden design Meerut, farmhouse landscaping Meerut, terrace garden Meerut",
    heroLabel: "Serving Meerut",
    h1: "Landscape Design in Meerut",
    intro: [
      "Garvaana Enterprise extends its luxury landscape design and garden management services to Meerut — designing villa gardens, farmhouse landscapes, terrace gardens and lawns for homeowners and businesses across the city.",
      "From design and installation to premium planting and ongoing maintenance, we bring the same standard of horticultural excellence we deliver across Delhi NCR to Meerut and its surrounding areas.",
    ],
    localities: [
      "Shastri Nagar",
      "Ganga Nagar",
      "Pallavpuram",
      "Modipuram",
      "Meerut Cantt",
      "Delhi Road belt",
    ],
    featuredProject: {
      name: "Delhi NCR Villa & Farmhouse Portfolio",
      blurb:
        "We bring the design language of our Gurugram and Noida villa projects — structured lawns, pergola terraces, water features and curated planting — to homes and farmhouses in Meerut.",
    },
    services: [
      "Villa & farmhouse garden design",
      "Terrace & rooftop gardens",
      "Lawn installation & turf laying",
      "Drip & sprinkler irrigation",
      "Garden lighting & water features",
      "Garden maintenance contracts",
    ],
    faqs: [
      {
        q: "Does Garvaana provide landscape services in Meerut?",
        a: "Yes — we design and maintain villa gardens, farmhouse landscapes, terrace gardens and lawns across Meerut, including Shastri Nagar, Ganga Nagar, Pallavpuram, Modipuram and Meerut Cantt.",
      },
      {
        q: "Do you do garden maintenance in Meerut?",
        a: "Yes. We offer monthly, quarterly and annual garden maintenance contracts in Meerut covering pruning, fertilisation, pest control and seasonal care.",
      },
      {
        q: "How do I book a consultation for a garden in Meerut?",
        a: "Call or WhatsApp +91-9800040505 for a free consultation. We assess the site and send a written quote within 24 hours.",
      },
    ],
  },

  /* ── GAJRAULA — HQ + plant nursery (home base) ── */
  {
    slug: "landscape-design-gajraula",
    city: "Gajraula",
    region: "Uttar Pradesh",
    priority: 0.9,
    title: "Landscape Design & Plant Nursery in Gajraula — Garvaana",
    description:
      "Garvaana Enterprise — our HQ & 500+ variety plant nursery in Gajraula, Amroha. Landscape design, garden installation, wholesale plants & garden maintenance across Gajraula & Amroha. Call +91-9800040505.",
    keywords:
      "landscape design in Gajraula, landscape designers in Gajraula, garden designers in Gajraula, plant nursery in Gajraula, wholesale plant nursery Gajraula, garden maintenance in Gajraula, landscape services in Gajraula, nursery in Amroha, plant nursery Amroha, garden contractor Gajraula, best landscape designers in Gajraula",
    heroLabel: "Our Home Base",
    h1: "Landscape Design & Plant Nursery in Gajraula",
    intro: [
      "Gajraula, in Amroha district on the Delhi–Lucknow highway (NH-9), is home to Garvaana Enterprise — our headquarters and 500+ variety plant nursery. From here we design, grow and dispatch plants and complete landscapes across Delhi NCR and Pan India.",
      "For homeowners, farmhouses, schools and businesses in Gajraula and Amroha, we offer the full range — landscape design, garden installation, lawn laying, irrigation, wholesale and retail plants from our own nursery, and year-round garden maintenance.",
    ],
    localities: [
      "Hasanpur Road & Manota",
      "Gajraula Town",
      "Amroha",
      "Hasanpur",
      "Dhanaura",
      "Joya",
    ],
    featuredProject: {
      name: "Garvaana Nursery & HQ — Vill. Manota, Hasanpur Road",
      blurb:
        "Our home nursery in Gajraula stocks 500+ plant varieties — palms, bonsai, topiary, hedges, flowering plants, shrubs and specimen trees — supplying our own projects and customers across the region at wholesale and retail.",
    },
    services: [
      "Landscape & garden design",
      "Plant nursery — 500+ varieties (wholesale & retail)",
      "Garden installation & lawn laying",
      "Drip & sprinkler irrigation",
      "Farmhouse & kitchen gardens",
      "Garden maintenance contracts",
    ],
    faqs: [
      {
        q: "Where is Garvaana's plant nursery located in Gajraula?",
        a: "Our nursery and headquarters are at Vill - Manota, Hasanpur Road, Gajraula, Amroha, Uttar Pradesh 244241. We stock 500+ plant varieties for wholesale and retail. Call +91-9800040505 before visiting.",
      },
      {
        q: "Do you sell plants wholesale from your Gajraula nursery?",
        a: "Yes. We supply plants wholesale and retail from our Gajraula nursery — palms, topiary, hedges, flowering plants, shrubs and specimen trees — to homeowners, landscapers, builders and architects, with trade pricing for bulk orders.",
      },
      {
        q: "Do you provide landscape design and garden maintenance in Gajraula and Amroha?",
        a: "Yes — being our home base, we offer the full range of landscape design, installation and maintenance services across Gajraula, Amroha and surrounding areas, plus easy access to our on-site nursery.",
      },
    ],
  },
];

/* ================================================================
   EXTENDED SERVICE AREAS — cities within ~500 km of Delhi NCR that
   Garvaana serves (matches the areaServed list in index.html schema).
   These generate lighter, useful service-area pages (design + garden).
================================================================ */
const EXTENDED_CITIES = [
  { city: "Lucknow", state: "Uttar Pradesh", localities: ["Gomti Nagar", "Hazratganj", "Indira Nagar", "Aliganj", "Mahanagar", "Jankipuram"], known: "the City of Nawabs" },
  { city: "Agra", state: "Uttar Pradesh", localities: ["Taj Ganj", "Dayalbagh", "Kamla Nagar", "Sikandra", "Civil Lines", "Fatehabad Road"], known: "home of the Taj Mahal" },
  { city: "Mathura", state: "Uttar Pradesh", localities: ["Krishna Nagar", "Govardhan Road", "Civil Lines", "Masani", "Vrindavan Road"], known: "the sacred Braj region" },
  { city: "Vrindavan", state: "Uttar Pradesh", localities: ["Raman Reti", "Chhatikara", "Parikrama Marg", "Vrindavan Town"], known: "the temple town of Braj" },
  { city: "Aligarh", state: "Uttar Pradesh", localities: ["Civil Lines", "Ramghat Road", "Marris Road", "Sasni Gate"] },
  { city: "Moradabad", state: "Uttar Pradesh", localities: ["Civil Lines", "Buddhi Vihar", "Majhola", "Delhi Road"], known: "the Brass City" },
  { city: "Bareilly", state: "Uttar Pradesh", localities: ["Civil Lines", "Rajendra Nagar", "Pilibhit Road", "Cantt"] },
  { city: "Amroha", state: "Uttar Pradesh", localities: ["Gajraula", "Hasanpur", "Dhanaura", "Amroha Town"] },
  { city: "Saharanpur", state: "Uttar Pradesh", localities: ["Court Road", "Delhi Road", "Ambala Road", "Janakpuri"] },
  { city: "Muzaffarnagar", state: "Uttar Pradesh", localities: ["Civil Lines", "New Mandi", "Jansath Road", "Roorkee Road"] },
  { city: "Chandigarh", state: "Chandigarh", localities: ["Sector 17", "Sector 22", "Sector 35", "Panchkula", "Mohali"], known: "The City Beautiful" },
  { city: "Karnal", state: "Haryana", localities: ["Sector 12", "Model Town", "Kunjpura Road", "Mughal Canal"] },
  { city: "Panipat", state: "Haryana", localities: ["Model Town", "Sector 11-12", "GT Road", "Tehsil Camp"] },
  { city: "Sonipat", state: "Haryana", localities: ["Model Town", "Sector 14-15", "Murthal", "Kundli"] },
  { city: "Rohtak", state: "Haryana", localities: ["Model Town", "Sector 1-3", "Delhi Road", "HUDA Sectors"] },
  { city: "Ambala", state: "Haryana", localities: ["Ambala Cantt", "Ambala City", "Mahesh Nagar", "Baldev Nagar"] },
  { city: "Hisar", state: "Haryana", localities: ["Model Town", "Urban Estate", "Sector 13-14", "Mehta Nagar"] },
  { city: "Rewari", state: "Haryana", localities: ["Model Town", "Sector 3-4", "Circular Road", "Bhiwadi Road"] },
  { city: "Jaipur", state: "Rajasthan", localities: ["Malviya Nagar", "Vaishali Nagar", "C-Scheme", "Mansarovar", "Jagatpura", "Civil Lines"], known: "the Pink City" },
  { city: "Alwar", state: "Rajasthan", localities: ["Scheme 1-8", "Moti Doongri", "Tijara Road", "Bhagat Singh Circle"] },
  { city: "Bharatpur", state: "Rajasthan", localities: ["Civil Lines", "Bhagat Singh Colony", "Agra Road", "Anah Gate"] },
  { city: "Ajmer", state: "Rajasthan", localities: ["Vaishali Nagar", "Civil Lines", "Pushkar Road", "Panchsheel"] },
  { city: "Jodhpur", state: "Rajasthan", localities: ["Ratanada", "Sardarpura", "Shastri Nagar", "Paota"], known: "the Blue City" },
  { city: "Udaipur", state: "Rajasthan", localities: ["Hiran Magri", "Bhupalpura", "Sector 11-14", "Fatehpura"], known: "the City of Lakes" },
  { city: "Dehradun", state: "Uttarakhand", localities: ["Rajpur Road", "Vasant Vihar", "Dalanwala", "Clement Town", "Sahastradhara Road"] },
  { city: "Haridwar", state: "Uttarakhand", localities: ["Jwalapur", "Kankhal", "Ranipur", "BHEL Township"] },
  { city: "Rishikesh", state: "Uttarakhand", localities: ["Tapovan", "Muni Ki Reti", "Lakshman Jhula", "IDPL"] },
  { city: "Roorkee", state: "Uttarakhand", localities: ["Civil Lines", "Ramnagar", "BT Ganj", "Solani Vihar"] },
  { city: "Haldwani", state: "Uttarakhand", localities: ["Kathgodam", "Mukhani", "Kaladhungi Road", "Rampur Road"] },
];

/* ================================================================
   PAGE GENERATION
   Two page families per city, each with distinct intent + content so
   Google treats them as separate genuine pages (not doorway clones):
     · "design"  → /landscape-design-<city>   (design & build)
     · "garden"  → /garden-services-<city>     (maintenance & AMC)
================================================================ */

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/* CORE design pages: enrich the hand-written entries above with type + citySlug */
const DESIGN_PAGES = LOCATIONS.map((loc) => ({
  ...loc,
  type: "design",
  citySlug: loc.slug.replace("landscape-design-", ""),
}));

/* EXTENDED design pages: lighter, generated for service-area cities.
   No fabricated localities — LocationPage simply hides that block. */
function makeExtendedDesignPage({ city, state, localities, known }) {
  const citySlug = slugify(city);
  const areas = localities || [];
  const areaPhrase =
    areas.length >= 3
      ? `from ${areas[0]} and ${areas[1]} to ${areas[2]} and beyond`
      : `across the city`;
  const knownPhrase = known ? ` — ${known}` : "";
  return {
    type: "design",
    citySlug,
    slug: `landscape-design-${citySlug}`,
    city,
    region: state,
    priority: 0.6,
    title: `Landscape Design in ${city} | Garden Designers — Garvaana`,
    description: `Garvaana — landscape designers serving ${city}, ${state}${
      areas.length ? ` (${areas.slice(0, 3).join(", ")} & more)` : ""
    }. Villa & farmhouse gardens, terrace gardens, lawns, irrigation & 500+ plant nursery supply. Call +91-9800040505.`,
    keywords: `landscape design in ${city}, landscape designers in ${city}, landscape services in ${city}, garden designers in ${city}, garden design services in ${city}, garden maintenance in ${city}, best landscape designers in ${city}, luxury landscape design in ${city}, garden contractor in ${city}, villa garden design ${city}, terrace garden ${city}`,
    heroLabel: `Serving ${city}, ${state}`,
    h1: `Landscape Design in ${city}`,
    intro: [
      `Garvaana Enterprise brings luxury landscape design and garden management to ${city}${knownPhrase}, ${state}. We design, build and maintain villa gardens, farmhouse landscapes, terrace gardens, lawns and water features for homes and businesses ${areaPhrase}.`,
      `As part of our Delhi NCR and Pan-India network, ${city} clients get the full journey — 3D design, installation, premium planting from our own 500+ variety nursery, smart irrigation and ongoing maintenance — delivered by a dedicated team deployed to the city.`,
    ],
    localities: areas.length ? areas : null,
    featuredProject: null,
    services: [
      "Villa & farmhouse garden design",
      "Terrace & rooftop gardens",
      "Lawn installation & turf laying",
      "Drip & sprinkler irrigation",
      "Garden lighting & water features",
      "Plant supply from our nursery",
    ],
    faqs: [
      {
        q: `Does Garvaana provide landscape design in ${city}?`,
        a: `Yes. Garvaana Enterprise serves ${city}, ${state}${
          areas.length ? ` — including ${areas.slice(0, 4).join(", ")}` : ""
        } — designing villa gardens, farmhouse landscapes, terrace gardens and lawns, with plant supply from our own nursery.`,
      },
      {
        q: `Do you offer garden maintenance in ${city}?`,
        a: `Yes — we offer scheduled garden maintenance and annual contracts (AMC) in ${city}. See our Garden Maintenance & Services in ${city} page, or call +91-9800040505.`,
      },
      {
        q: `How do I get a quote for a garden in ${city}?`,
        a: `Call or WhatsApp +91-9800040505 for a free consultation. For ${city} projects we discuss your requirements, plan a site visit and send a written quote.`,
      },
    ],
  };
}

/* Garden-services pages: maintenance-focused, generated from each city's
   base data (real localities + featured project) for local uniqueness. */
function makeGardenPage(base) {
  const c = base.city;
  const areas = base.localities || [];
  const areaPhrase = areas.length >= 2 ? ` — including ${areas.slice(0, 3).join(", ")}` : "";
  return {
    type: "garden",
    citySlug: base.citySlug,
    slug: `garden-services-${base.citySlug}`,
    city: c,
    region: base.region,
    priority: Math.max(0.6, (base.priority || 0.8) - 0.05),
    title: `Garden Maintenance & Services in ${c} — Garvaana`,
    description: `Garvaana — professional garden maintenance & services in ${c}. Monthly & annual AMC, lawn care, pruning, pest control and society & villa garden upkeep. Call +91-9800040505.`,
    keywords: `garden maintenance in ${c}, garden services in ${c}, garden care in ${c}, lawn care ${c}, lawn maintenance ${c}, AMC garden maintenance ${c}, monthly garden maintenance ${c}, annual garden maintenance ${c}, society garden maintenance ${c}, villa garden maintenance ${c}, garden cleaning ${c}, garden upkeep services ${c}, professional gardener ${c}, garden contractor in ${c}`,
    heroLabel: `Garden Care in ${c}`,
    h1: `Garden Maintenance & Services in ${c}`,
    intro: [
      `Garvaana Enterprise provides professional garden maintenance and upkeep across ${c}, ${base.region}${areaPhrase}. Our trained horticulturists keep your garden lush and healthy all year — pruning, fertilisation, pest and disease control, lawn care and seasonal cleanups — for villas, apartments, societies and commercial properties.`,
      `Choose monthly, quarterly or annual garden maintenance contracts (AMC) tailored to your space. From a single villa lawn to an entire housing society's common areas in ${c}, our team handles every detail so your green spaces always look their best.`,
    ],
    localities: base.localities,
    featuredProject: base.featuredProject,
    services: [
      "Monthly, quarterly & annual AMC",
      "Lawn mowing, edging & care",
      "Pruning, hedging & topiary upkeep",
      "Fertilisation & soil health management",
      "Pest, disease & weed control",
      "Seasonal cleanup & replanting",
    ],
    faqs: [
      {
        q: `Do you offer annual garden maintenance contracts (AMC) in ${c}?`,
        a: `Yes. We offer monthly, quarterly and annual garden maintenance contracts (AMC) in ${c} for villas, apartments, housing societies and commercial properties — covering all routine care.`,
      },
      {
        q: `What does a garden maintenance visit include in ${c}?`,
        a: `Each visit typically includes lawn mowing and edging, pruning and hedge trimming, weeding, fertilisation, pest and disease checks, and clearing of debris — adjusted seasonally for ${c}'s climate.`,
      },
      {
        q: `Do you maintain housing-society common-area gardens in ${c}?`,
        a: `Yes — we maintain society and RWA common-area gardens across ${c}, including lawns, avenue trees, hedges and seasonal planting, under scheduled maintenance contracts.`,
      },
    ],
  };
}

/* ── PAN-INDIA page — services offered nationwide ── */
const NATIONAL_PAGE = {
  type: "design",
  national: true,
  citySlug: "india",
  slug: "landscape-design-india",
  city: "India",
  region: "Pan India",
  priority: 0.85,
  title: "Luxury Landscape Design Company in India — Garvaana",
  description:
    "Garvaana Enterprise — India's premium landscape design & garden management company. Pan-India villa gardens, resort & corporate landscaping, wholesale plant supply & 500+ variety nursery. Call +91-9800040505.",
  keywords:
    "luxury landscape design India, best landscape designers in India, landscape design company India, top landscape design firm India, garden design company India, wholesale plants supplier India, plant supplier for architects, best plant nursery in India, landscape contractor India, luxury garden design India, pan India landscape services, bulk plants supplier India",
  heroLabel: "Pan-India Services",
  h1: "Luxury Landscape Design Company in India",
  intro: [
    "Garvaana Enterprise is a premium luxury landscape design and garden management company serving clients across India. While our design studios and 500+ variety nursery are based in Delhi NCR and Gajraula (Uttar Pradesh), we deliver landscape design, large-project execution and wholesale plant supply nationwide.",
    "For Pan-India clients we provide 3D design and remote consultation, wholesale and bulk plant supply (palms, topiary, specimen trees and more) to landscapers, builders and architects, and dedicated on-site execution teams for villa, resort, corporate and township projects anywhere in the country.",
  ],
  localities: null,
  featuredProject: null,
  services: [
    "Luxury landscape & garden design",
    "Resort, hotel & corporate landscaping",
    "Township & large-project execution",
    "Wholesale & bulk plant supply (nationwide)",
    "Trade supply for builders & architects",
    "3D design & remote consultation",
  ],
  faqs: [
    {
      q: "Does Garvaana provide landscape services across all of India?",
      a: "Yes. Although based in Delhi NCR and Gajraula (UP), Garvaana Enterprise serves clients Pan-India — offering landscape design, large-project execution and nationwide wholesale plant supply.",
    },
    {
      q: "Can you supply plants in bulk anywhere in India?",
      a: "Yes — we supply 500+ plant varieties wholesale and in bulk across India to landscapers, builders, architects and developers, with trade pricing. Call +91-9800040505 for a quote.",
    },
    {
      q: "Do you take on landscape projects outside Delhi NCR?",
      a: "Yes. For villa, resort, corporate and township projects across India we deploy a dedicated execution team and provide remote design and consultation. Contact us to discuss your project.",
    },
  ],
};

/* ── Shared garden-maintenance offering, folded into every city page ── */
const GARDEN_SERVICES = [
  "Monthly, quarterly & annual AMC",
  "Lawn mowing, edging & care",
  "Pruning, hedging & topiary upkeep",
  "Fertilisation & soil health management",
  "Pest, disease & weed control",
  "Seasonal cleanup & replanting",
];

/* Short marketing blurb shown on the Service-Areas city cards */
function cityBlurb(base) {
  const c = base.city;
  const areas = base.localities || [];
  const knownBit = base.known ? `, ${base.known},` : "";
  const areaBit = areas.length >= 2 ? ` Serving ${areas.slice(0, 3).join(", ")} & beyond.` : "";
  return `Luxury landscape design, garden installation & year-round care in ${c}${knownBit} — villa & terrace gardens, lawns, water features and 500+ nursery plants.${areaBit}`;
}

/* Turn a city "base" (core hand-written OR extended generated OR national)
   into ONE combined page covering Landscape Design + Garden Maintenance. */
function makeCombinedPage(base) {
  const c = base.city;
  const cs = base.citySlug || slugify(c);
  const national = !!base.national;
  return {
    type: "city",
    national,
    citySlug: cs,
    slug: national ? "landscape-design-india" : `landscape-design-${cs}`,
    city: c,
    region: base.region,
    priority: base.priority || 0.7,
    title: national ? base.title : `Landscape Design & Garden Maintenance in ${c} — Garvaana`,
    description: national
      ? base.description
      : `Garvaana — landscape design, garden installation & maintenance in ${c}, ${base.region}. Villa & terrace gardens, lawns, irrigation, 500+ nursery plants & annual garden care (AMC). Call +91-9800040505.`,
    keywords: national
      ? base.keywords
      : `${base.keywords}, garden maintenance in ${c}, garden services in ${c}, AMC garden maintenance ${c}, lawn care ${c}, garden upkeep ${c}`,
    heroLabel: base.heroLabel,
    h1: national ? base.h1 : `Landscape Design & Garden Maintenance in ${c}`,
    intro: base.intro,
    localities: base.localities || null,
    featuredProject: base.featuredProject || null,
    blurb: cityBlurb(base),
    designServices: base.services,
    gardenServices: national ? null : GARDEN_SERVICES,
    faqs: national
      ? base.faqs
      : [
          ...base.faqs,
          {
            q: `Do you offer garden maintenance (AMC) in ${c}?`,
            a: `Yes — Garvaana offers monthly, quarterly and annual garden maintenance contracts (AMC) in ${c}, covering lawn care, pruning, fertilisation, pest control and seasonal cleanup for homes, villas, societies and commercial properties.`,
          },
        ],
  };
}

/* One combined page per city (+ the national page) */
const CORE_CITY_SLUGS = new Set(DESIGN_PAGES.map((d) => d.citySlug));
const EXTENDED_BASES = EXTENDED_CITIES.map(makeExtendedDesignPage);
const CITY_PAGES = [...DESIGN_PAGES, ...EXTENDED_BASES].map(makeCombinedPage);
const NATIONAL_COMBINED = makeCombinedPage(NATIONAL_PAGE);

export const ALL_LOCATION_PAGES = [NATIONAL_COMBINED, ...CITY_PAGES];

/* Quick lookup by slug, used by the route + SEO component */
export const LOCATION_BY_SLUG = ALL_LOCATION_PAGES.reduce((acc, p) => {
  acc[`/${p.slug}`] = p;
  return acc;
}, {});

/* 301-style redirects: old separate garden URLs → the combined city page */
export const REDIRECTS = CITY_PAGES.reduce((acc, p) => {
  acc[`/garden-services-${p.citySlug}`] = `/${p.slug}`;
  return acc;
}, {});

/* City link rows — single combined link + marketing blurb */
const toLink = (p) => ({ city: p.city, region: p.region, slug: p.slug, blurb: p.blurb });
export const CITY_LINKS = CITY_PAGES.filter((p) => CORE_CITY_SLUGS.has(p.citySlug)).map(toLink);
export const EXTENDED_CITY_LINKS = CITY_PAGES.filter((p) => !CORE_CITY_SLUGS.has(p.citySlug)).map(toLink);

/* Pan-India page link */
export const NATIONAL_LINK = { city: "India", slug: NATIONAL_COMBINED.slug };

/* ================================================================
   PHYSICAL OFFICES — used by the maps on Service Areas & Contact
================================================================ */
export const OFFICES = [
  {
    key: "hq",
    name: "Headquarters & Plant Nursery",
    city: "Gajraula",
    address: "Vill - Manota, Hasanpur Road, Gajraula, Amroha, Uttar Pradesh 244241",
    lat: 28.7766782,
    lng: 78.2684089,
    mapsUrl: "https://maps.app.goo.gl/Q1n2ZzazmT9F1xRV9",
    embed: "https://www.google.com/maps?q=28.7766782,78.2684089&z=16&hl=en&output=embed",
  },
  {
    key: "noida",
    name: "Noida Studio & Office",
    city: "Noida",
    address: "Noida, Uttar Pradesh",
    lat: 28.5581819,
    lng: 77.3410319,
    mapsUrl: "https://maps.app.goo.gl/bi8nuzst52Fc6Lo47",
    embed: "https://www.google.com/maps?q=28.5581819,77.3410319&z=16&hl=en&output=embed",
  },
];

/* ================================================================
   SERVICE AREAS, grouped by region — powers the /service-areas hub
   page and the nav dropdown. Core NCR cities first, then by state.
================================================================ */
const CORE_AREA_LINKS = CITY_LINKS.map((c) => ({ ...c, group: "Delhi NCR" }));
const EXT_AREA_LINKS = EXTENDED_CITY_LINKS.map((c) => ({ ...c, group: c.region }));
const ALL_AREA_LINKS = [...CORE_AREA_LINKS, ...EXT_AREA_LINKS];

const REGION_ORDER = [
  "Delhi NCR",
  "Uttar Pradesh",
  "Haryana",
  "Rajasthan",
  "Uttarakhand",
  "Chandigarh",
];

export const SERVICE_AREAS = REGION_ORDER.map((region) => ({
  region,
  cities: ALL_AREA_LINKS.filter((c) => c.group === region),
})).filter((g) => g.cities.length);

/* Compact list for the nav dropdown — core cities + an "all areas" tail */
export const NAV_AREA_CITIES = CITY_LINKS.map((c) => ({
  city: c.city,
  slug: c.slug,
}));
